// Small local helpers inlined from the shade utils.

type ClassValue = string | false | null | undefined;

export function classNames(...classes: ClassValue[]): string {
  return classes.filter(Boolean).join(" ");
}

export const isNumber = (val: unknown): val is number => {
  return typeof val === "number" && !Number.isNaN(val);
};

export const inputElementIsActive = (): boolean => {
  const element = document?.activeElement as HTMLElement | null;
  if (!element) return false;
  const tagsToIgnore = ["INPUT", "TEXTAREA", "SELECT"];
  return tagsToIgnore.includes(element.tagName) || element.isContentEditable;
};

export const getUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "uuid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

// Safari and legacy Edge expose the fullscreen API under vendor prefixes.
// Narrow types so we don't reach for `as any`.
interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

const isDocumentFullScreen = (): boolean => {
  const d = document as FullscreenDocument;
  return !!(
    d.fullscreenElement ||
    d.webkitFullscreenElement ||
    d.msFullscreenElement
  );
};

export const toggleElementFullScreen = (e: HTMLElement): void => {
  try {
    const el = e as FullscreenElement;
    const d = document as FullscreenDocument;
    if (!isDocumentFullScreen()) {
      const request =
        el.requestFullscreen ?? el.webkitRequestFullscreen ?? el.msRequestFullscreen;
      request?.call(el);
    } else {
      const exit = d.exitFullscreen ?? d.webkitExitFullscreen ?? d.msExitFullscreen;
      exit?.call(d);
    }
  } catch {
    /* swallow fullscreen errors */
  }
};
